/* eslint-disable no-param-reassign, no-restricted-syntax, no-plusplus, no-cond-assign */
/**
 * Utility functions for working with gross code templates
 *
 * Gross code templates contain underscores (_) as placeholders that should be
 * replaced with extracted dimensions.
 *
 * Example:
 *   Template: "measuring _ x _ x _ mm"
 *   Dimensions: "0.4 x 0.3 x 0.1"
 *   Result: "measuring 0.4 x 0.3 x 0.1 mm"
 */

/**
 * Convert dimensions from cm to mm with intelligent detection
 *
 * Heuristic: If all dimension values are < 10, assume they are in cm and convert to mm.
 * If any value >= 10, assume already in mm.
 *
 * This works because:
 * - GI biopsies are typically < 10mm (reported as 0.4 cm, 0.3 cm, etc.)
 * - Larger specimens > 10mm are usually reported in mm already
 *
 * @param dimensions - The dimensions string (e.g., "0.4 x 0.3 x 0.1" or "12 x 8 x 5")
 * @returns Dimensions converted to mm with consistent formatting
 */
export function convertDimensionsToMm(dimensions: string): string {
  if (!dimensions) {
    return "";
  }

  // Parse dimensions: "0.4 x 0.3 x 0.1" → [0.4, 0.3, 0.1]
  const values = dimensions
    .split(/\s*[xX×]\s*/)
    .map((d) => {
      d = d.trim();
      // Handle leading decimal point: ".4" → "0.4"
      if (d.startsWith(".")) {
        d = `0${d}`;
      }
      return parseFloat(d);
    })
    .filter((v) => !isNaN(v));

  if (values.length === 0) {
    return dimensions;
  }

  // Check if all values < 10 (likely in cm)
  const likelyCm = values.every((v) => v < 10);

  if (likelyCm) {
    // Convert cm to mm (multiply by 10)
    return values.map((v) => (v * 10).toFixed(1)).join(" x ");
  }

  // Already in mm, just format consistently
  return values.map((v) => v.toFixed(1)).join(" x ");
}

/**
 * Fill a gross code template with dimensions
 *
 * @param template - The gross description template with underscores as placeholders
 * @param dimensions - The dimensions string (e.g., "0.4 x 0.3 x 0.1" or ".4 x .3 x .1")
 * @param autoConvert - If true, automatically convert from cm to mm (default: true)
 * @returns The template with placeholders replaced by dimensions
 */
export function fillGrossTemplate(
  template: string,
  dimensions: string,
  autoConvert = true
): string {
  if (!template || !dimensions) {
    return template || "";
  }

  // Convert to mm if needed
  const finalDimensions = autoConvert
    ? convertDimensionsToMm(dimensions)
    : dimensions;

  // Parse dimensions - handle various formats
  // "4.0 x 3.0 x 1.0" after conversion
  const dimArray = finalDimensions
    .split(/\s*[xX×]\s*/) // Split by x, X, or × with optional spaces
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

  if (dimArray.length === 0) {
    return template;
  }

  // Replace each underscore with a dimension value
  let result = template;
  for (const dim of dimArray) {
    // Find the first underscore and replace it
    const index = result.indexOf("_");
    if (index === -1) {
      break; // No more underscores to replace
    }
    result = result.substring(0, index) + dim + result.substring(index + 1);
  }

  return result;
}

/**
 * Extract placeholder positions from a gross template
 * Useful for highlighting or identifying where dimensions should go
 *
 * @param template - The gross description template
 * @returns Array of placeholder positions and context
 */
export function extractPlaceholders(template: string): Array<{
  index: number;
  position: number;
  context: string;
}> {
  if (!template) {
    return [];
  }

  const placeholders: Array<{
    index: number;
    position: number;
    context: string;
  }> = [];

  let placeholderIndex = 0;

  for (let i = 0; i < template.length; i++) {
    if (template[i] === "_") {
      // Get context around the placeholder (10 chars before and after)
      const start = Math.max(0, i - 10);
      const end = Math.min(template.length, i + 11);
      const context = template.substring(start, end);

      placeholders.push({
        index: placeholderIndex++,
        position: i,
        context: context.trim(),
      });
    }
  }

  return placeholders;
}

/**
 * Count the number of dimension placeholders in a template
 *
 * @param template - The gross description template
 * @returns Number of underscore placeholders
 */
export function countPlaceholders(template: string): number {
  if (!template) {
    return 0;
  }
  return (template.match(/_/g) || []).length;
}

/**
 * Validate that dimensions match the expected number of placeholders
 *
 * @param template - The gross description template
 * @param dimensions - The dimensions string
 * @returns true if the number of dimensions matches the number of placeholders
 */
export function validateDimensions(
  template: string,
  dimensions: string
): boolean {
  const placeholderCount = countPlaceholders(template);
  const dimensionCount = dimensions
    .split(/\s*[xX×]\s*/)
    .filter((d) => d.trim().length > 0).length;

  return placeholderCount === dimensionCount;
}

/**
 * Format dimensions to a standard format
 * Converts formats like ".4 x .3 x .1" to "0.4 x 0.3 x 0.1"
 *
 * @param dimensions - Raw dimensions string
 * @returns Formatted dimensions string
 */
export function formatDimensions(dimensions: string): string {
  if (!dimensions) {
    return "";
  }

  return dimensions
    .split(/\s*[xX×]\s*/)
    .map((d) => {
      d = d.trim();
      // Add leading zero if starts with decimal point
      if (d.startsWith(".")) {
        d = `0${d}`;
      }
      return d;
    })
    .join(" x ");
}

/**
 * Fill a gross code template with dimensions and wrap them in HTML <mark> tags for highlighting
 *
 * @param template - The gross description template with underscores as placeholders
 * @param dimensions - The dimensions string (e.g., "0.4 x 0.3 x 0.1" or ".4 x .3 x .1")
 * @param autoConvert - If true, automatically convert from cm to mm (default: true)
 * @returns HTML string with dimensions wrapped in <mark class="dimension-highlight"> tags
 */
export function fillGrossTemplateWithHighlights(
  template: string,
  dimensions: string,
  autoConvert = true
): string {
  if (!template || !dimensions) {
    return template || "";
  }

  // Convert to mm if needed
  const finalDimensions = autoConvert
    ? convertDimensionsToMm(dimensions)
    : dimensions;

  // Parse dimensions
  const dimArray = finalDimensions
    .split(/\s*[xX×]\s*/)
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

  if (dimArray.length === 0) {
    return template;
  }

  // Replace each underscore with a highlighted dimension value using inline styles
  let result = template;
  for (const dim of dimArray) {
    // Find the first underscore and replace it with highlighted value
    const index = result.indexOf("_");
    if (index === -1) {
      break; // No more underscores to replace
    }
    // Use inline styles instead of mark tags for better Tiptap compatibility
    const highlighted = `<span style="background-color: #ffd43b; padding: 2px 4px; border-radius: 3px; font-weight: 600; color: #1a1a1a;">${dim}</span>`;
    result =
      result.substring(0, index) + highlighted + result.substring(index + 1);
  }

  return result;
}

/**
 * Normalizes a legacy gross template by converting underscores and bracketed
 * placeholders into {{variable}} tokens.
 */
export function normalizeLegacyTemplate(template: string): string {
  if (!template) return "";

  let result = template;

  // 1. Replace [ Bodysite ] and [ Subsite ] with system variables
  result = result.replace(/\[\s*Bodysite\s*\]/gi, "{{source}}");
  result = result.replace(/\[\s*Subsite\s*\]/gi, "{{subsite}}");
  result = result.replace(/\[\s*(?:anatomical\s+)?site\s*\]/gi, "{{source}}");
  result = result.replace(/\[\s*Body\s*site\s*\]/gi, "{{source}}");
  result = result.replace(/\[\s*location\s*\]/gi, "{{location}}");

  // 2. Replace other common brackets
  result = result.replace(/\[\s*Size\s*\]/gi, "{{size1}}");
  result = result.replace(/\[\s*Descriptive\s*\]/gi, "{{descriptive}}");

  // 3. Handle dimension groups like "_ x _ x _" or "_ x _"
  let sizeIndex = 1;

  // Find all "{{sizeN}}" to start numbering from the next one
  const existingSizes = result.match(/\{\{size(\d+)\}\}/gi);
  if (existingSizes) {
    existingSizes.forEach((s) => {
      const match = s.match(/\d+/);
      if (match) {
        const num = parseInt(match[0], 10);
        if (num >= sizeIndex) sizeIndex = num + 1;
      }
    });
  }

  // Replace dimension groups
  // Regex for _ x _ x _ or _ x _ or _x_x_
  const dimRegex = /_\s*[xX×]\s*_\s*(?:[xX×]\s*_)?/g;
  result = result.replace(dimRegex, (match) => {
    const underscores = match.match(/_/g);
    if (!underscores) return match;

    const chips = underscores.map(() => `{{size${sizeIndex++}}}`);
    // Reconstruct with the same separators
    let i = 0;
    return match.replace(/_/g, () => chips[i++]);
  });

  // 4. Handle specific lone placeholders
  // Fragment count pattern
  result = result.replace(
    /_\s*tissue fragment\(s\)/gi,
    "{{fragments}} tissue fragment(s)"
  );
  result = result.replace(/_\s*fragment\(s\)/gi, "{{fragments}} fragment(s)");
  result = result.replace(/indicates\s*_\s*/gi, "indicates {{fragments}} ");

  // Handle lone underscores
  // If it's the first one and near "labeled with" + "and", it's likely the specimen letter
  if (
    result.includes("labeled with") &&
    result.includes("and") &&
    result.includes("_")
  ) {
    // Look for "_" or _ after "and"
    const andIndex = result.lastIndexOf("and");
    const firstUnderscoreAfterAnd = result.indexOf("_", andIndex);

    if (firstUnderscoreAfterAnd !== -1) {
      // Check if it's within a reasonable distance (e.g. 20 chars)
      if (firstUnderscoreAfterAnd - andIndex < 20) {
        // Replace it
        const prefix = result.substring(0, firstUnderscoreAfterAnd);
        const suffix = result.substring(firstUnderscoreAfterAnd + 1);

        // Handle quoted version
        if (prefix.endsWith('"') && suffix.startsWith('"')) {
          result = `${prefix.substring(
            0,
            prefix.length - 1
          )}"{{specimen_letter}}"${suffix.substring(1)}`;
        } else {
          result = `${prefix}{{specimen_letter}}${suffix}`;
        }
      }
    }
  }

  // Replace remaining lone underscores with unique variables
  let varIndex = 1;

  // Protect already normalized tokens ({{...}}) from being double-processed
  // during the final lone underscore replacement.
  const protectedTokens: string[] = [];
  result = result.replace(/\{\{[^}]+\}\}/g, (match) => {
    protectedTokens.push(match);
    return `@@TOKEN@${protectedTokens.length - 1}@@`;
  });

  result = result.replace(/_/g, () => `{{var${varIndex++}}}`);

  // Restore protected tokens
  protectedTokens.forEach((token, i) => {
    result = result.replace(`@@TOKEN@${i}@@`, token);
  });

  return result;
}

/**
 * Extract variable names from a gross code template description
 * Variables are defined using {{variable_name}} syntax
 *
 * @param description - The gross code description (can be plain text or HTML)
 * @returns Array of unique variable names found in the description
 *
 * @example
 * extractVariables("Specimen in {{container}} measuring {{dim1}} x {{dim2}}")
 * // Returns: ["container", "dim1", "dim2"]
 */
export function extractVariables(description: string): string[] {
  if (!description) {
    return [];
  }

  const regex = /\{\{([^}]+)\}\}/g;
  const variables = new Set<string>();
  let match;

  while ((match = regex.exec(description)) !== null) {
    variables.add(match[1].trim().toLowerCase());
  }

  return Array.from(variables);
}

/**
 * System variables that can be auto-replaced from context
 * These are variables defined in Linear C-1224
 */
export const SYSTEM_VARIABLES: Record<
  string,
  { description: string; contextKey: string }
> = {
  specimen_letter: {
    description: "Specimen designation (A, B, C)",
    contextKey: "specimenLetter",
  },
  specimen_count: {
    description: "Number of specimens in case",
    contextKey: "specimenCount",
  },
  source: {
    description: "Tissue source",
    contextKey: "sourceName",
  },
  subsite: {
    description: "Specific location/subsite",
    contextKey: "subsiteName",
  },
  collection_method: {
    description: "How specimen was collected",
    contextKey: "collectionMethod",
  },
  size_cm: {
    description: "Size in centimeters",
    contextKey: "sizeCm",
  },
  cassette_count: {
    description: "Number of cassettes",
    contextKey: "cassetteCount",
  },
  block_count: {
    description: "Number of blocks",
    contextKey: "blockCount",
  },
  fragment_count: {
    description: "Number of tissue fragments",
    contextKey: "fragmentCount",
  },
  fragments: {
    description: "Number of tissue fragments (alias for fragment_count)",
    contextKey: "fragmentCount",
  },
  bodysite: {
    description: "Tissue source (alias for source)",
    contextKey: "sourceName",
  },
  // Additional variables for diagnosis macros
  location: {
    description: "Anatomical location (same as subsite)",
    contextKey: "subsiteName",
  },
  patient_name: {
    description: "Patient full name",
    contextKey: "patientName",
  },
  case_number: {
    description: "Case number / accession number",
    contextKey: "caseNumber",
  },
  date_collected: {
    description: "Date specimen was collected",
    contextKey: "dateCollected",
  },
};

/**
 * Context object for system variable replacement
 */
export interface TemplateContext {
  specimenLetter?: string;
  specimenCount?: number;
  sourceName?: string;
  subsiteName?: string;
  collectionMethod?: string;
  sizeCm?: string;
  cassetteCount?: number;
  blockCount?: number;
  fragmentCount?: number;
  patientName?: string;
  caseNumber?: string;
  dateCollected?: string;
}

/**
 * Replace system variables in a template with values from context
 *
 * @param template - The template text with {{variable}} placeholders
 * @param context - Object containing values for system variables
 * @returns Template with system variables replaced
 */
export function replaceSystemVariables(
  template: string,
  context: TemplateContext
): string {
  if (!template) {
    return "";
  }

  let result = template;

  // Replace each system variable if its value is available in context
  for (const [varName, config] of Object.entries(SYSTEM_VARIABLES)) {
    const contextValue = context[config.contextKey as keyof TemplateContext];
    if (
      contextValue !== undefined &&
      contextValue !== null &&
      contextValue !== ""
    ) {
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, "gi");
      result = result.replace(regex, String(contextValue));
    }
  }

  return result;
}

/**
 * Replace a single variable in the template
 *
 * @param template - The template text
 * @param variableName - The variable name (without braces)
 * @param value - The value to replace with
 * @returns Template with the variable replaced
 */
export function replaceVariable(
  template: string,
  variableName: string,
  value: string
): string {
  if (!template || !variableName) {
    return template || "";
  }

  const regex = new RegExp(`\\{\\{${variableName}\\}\\}`, "gi");
  return template.replace(regex, value);
}

/**
 * Highlight unfilled variables in HTML content
 * Wraps {{variable}} patterns in warning-styled spans
 *
 * @param html - The HTML content
 * @returns HTML with unfilled variables highlighted in orange
 */
export function highlightUnfilledVariables(html: string): string {
  if (!html) {
    return "";
  }

  return html.replace(
    /\{\{([^}]+)\}\}/g,
    '<span style="background-color: #fff3cd; color: #856404; padding: 2px 4px; border-radius: 3px; font-weight: 500;">{{$1}}</span>'
  );
}

/**
 * Strip HTML tags from content for plain text operations
 *
 * @param html - HTML content
 * @returns Plain text content
 */
export function stripHtml(html: string): string {
  if (!html) {
    return "";
  }
  // Use a simple regex approach that works in both browser and tests
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
}

/**
 * Check if rich text content is empty (ignoring HTML tags)
 *
 * @param html - HTML content
 * @returns true if content is empty or only whitespace
 */
export function isRichTextEmpty(html: string): boolean {
  return stripHtml(html).trim() === "";
}
